import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import {
	usePatchMedicalRecordAssessment,
	usePatchMedicalRecordPlan,
} from '@/services';
import {
	resolveActivePlan,
	resolveLatestAssessment,
} from '@/pages/professional/sessao-chamada/utils/mapMedicalRecordToProntuario';
import { getErrorMessage } from '@/utils/errorMessage';
import {
	SessionFieldContainer,
	type ProntuarioEditorState,
	type ProntuarioFieldVariant,
} from './prontuarioEditableFields';

const SEVERITY_NONE = 'NONE';

const SECTION_TITLE_CLASS = {
	dark: 'font-albert text-[15px] font-semibold text-slate-100',
	light: 'font-albert text-[15px] font-semibold text-zinc-800',
} as const;

const EMPTY_SECTION_CLASS = {
	dark: 'rounded-xl border border-dashed border-slate-700 px-3.5 py-4 text-[13px] text-slate-500',
	light: 'rounded-xl border border-dashed border-zinc-300 px-3.5 py-4 text-[13px] text-zinc-400',
} as const;

type RecordFormState = {
	// Avaliação (histórico clínico)
	instruments_used: string;
	clinical_impression: string;
	icd10_primary: string;
	icd10_secondary: string;
	differential_diagnosis: string;
	bai_score: string;
	bai_level: string;
	bdi_score: string;
	bdi_level: string;
	// Plano terapêutico
	theoretical_approach: string;
	frequency: string;
	format: string;
	session_duration_min: string;
	estimated_duration: string;
	objectives: string;
	planned_techniques: string;
	referrals: string;
};

const ASSESSMENT_KEYS = [
	'instruments_used',
	'clinical_impression',
	'icd10_primary',
	'icd10_secondary',
	'differential_diagnosis',
	'bai_score',
	'bai_level',
	'bdi_score',
	'bdi_level',
] as const satisfies ReadonlyArray<keyof RecordFormState>;

const PLAN_KEYS = [
	'theoretical_approach',
	'frequency',
	'format',
	'session_duration_min',
	'estimated_duration',
	'objectives',
	'planned_techniques',
	'referrals',
] as const satisfies ReadonlyArray<keyof RecordFormState>;

const textValue = (value: string | null | undefined): string => value ?? '';
const numText = (value: number | null | undefined): string =>
	value == null ? '' : String(value);
const levelValue = (
	value: MedicalRecords.SymptomSeverityLevel | null | undefined,
): string => value ?? SEVERITY_NONE;

const buildInitialState = (
	assessment: MedicalRecords.Assessment | null,
	plan: MedicalRecords.Plan | null,
): RecordFormState => ({
	instruments_used: textValue(assessment?.instruments_used),
	clinical_impression: textValue(assessment?.clinical_impression),
	icd10_primary: textValue(assessment?.icd10_primary),
	icd10_secondary: textValue(assessment?.icd10_secondary),
	differential_diagnosis: textValue(assessment?.differential_diagnosis),
	bai_score: numText(assessment?.bai_score),
	bai_level: levelValue(assessment?.bai_level),
	bdi_score: numText(assessment?.bdi_score),
	bdi_level: levelValue(assessment?.bdi_level),
	theoretical_approach: textValue(plan?.theoretical_approach),
	frequency: textValue(plan?.frequency),
	format: textValue(plan?.format),
	session_duration_min: numText(plan?.session_duration_min),
	estimated_duration: textValue(plan?.estimated_duration),
	objectives: textValue(plan?.objectives),
	planned_techniques: textValue(plan?.planned_techniques),
	referrals: textValue(plan?.referrals),
});

const getChangedKeys = <K extends keyof RecordFormState>(
	keys: ReadonlyArray<K>,
	current: RecordFormState,
	baseline: RecordFormState,
): K[] => keys.filter((key) => current[key] !== baseline[key]);

const toNumberOrNull = (value: string): number | null => {
	const trimmed = value.trim();
	if (!trimmed) return null;
	const parsed = Number(trimmed);
	return Number.isFinite(parsed) ? parsed : null;
};

const toLevelOrNull = (
	value: string,
): MedicalRecords.SymptomSeverityLevel | null =>
	value === SEVERITY_NONE || value === ''
		? null
		: (value as MedicalRecords.SymptomSeverityLevel);

const buildAssessmentBody = (
	current: RecordFormState,
	baseline: RecordFormState,
): MedicalRecords.UpdateMedicalRecordAssessmentBody => {
	const changed = getChangedKeys(ASSESSMENT_KEYS, current, baseline);
	const body: MedicalRecords.UpdateMedicalRecordAssessmentBody = {};

	for (const key of changed) {
		switch (key) {
			case 'bai_score':
				body.bai_score = toNumberOrNull(current.bai_score);
				break;
			case 'bdi_score':
				body.bdi_score = toNumberOrNull(current.bdi_score);
				break;
			case 'bai_level':
				body.bai_level = toLevelOrNull(current.bai_level);
				break;
			case 'bdi_level':
				body.bdi_level = toLevelOrNull(current.bdi_level);
				break;
			default:
				body[key] = current[key] as never;
		}
	}

	return body;
};

const buildPlanBody = (
	current: RecordFormState,
	baseline: RecordFormState,
): MedicalRecords.UpdateMedicalRecordPlanBody => {
	const changed = getChangedKeys(PLAN_KEYS, current, baseline);
	const body: MedicalRecords.UpdateMedicalRecordPlanBody = {};

	for (const key of changed) {
		if (key === 'session_duration_min') {
			body.session_duration_min = toNumberOrNull(
				current.session_duration_min,
			);
		} else {
			body[key] = current[key] as never;
		}
	}

	return body;
};

type Props = {
	recordId: string;
	patientId: string;
	medicalRecord: MedicalRecords.MedicalRecord | null | undefined;
	isLoading?: boolean;
	isError?: boolean;
	variant?: ProntuarioFieldVariant;
	animateValues?: boolean;
	/** Demo/tutorial: não dispara PATCH na API. */
	demoMode?: boolean;
	onEditorStateChange?: (state: ProntuarioEditorState | null) => void;
};

const ProntuarioEditableMedicalRecordView = ({
	recordId,
	patientId,
	medicalRecord,
	isLoading = false,
	isError = false,
	variant = 'dark',
	animateValues = false,
	demoMode = false,
	onEditorStateChange,
}: Props) => {
	const queryClient = useQueryClient();

	const assessment = useMemo(
		() => resolveLatestAssessment(medicalRecord?.assessments),
		[medicalRecord?.assessments],
	);
	const plan = useMemo(
		() => resolveActivePlan(medicalRecord?.plans),
		[medicalRecord?.plans],
	);

	const [form, setForm] = useState<RecordFormState>(() =>
		buildInitialState(assessment, plan),
	);
	const [baselineForm, setBaselineForm] = useState<RecordFormState>(() =>
		buildInitialState(assessment, plan),
	);

	useEffect(() => {
		const next = buildInitialState(assessment, plan);
		setForm(next);
		setBaselineForm(next);
	}, [assessment, plan]);

	const invalidateRecord = useCallback(() => {
		void queryClient.invalidateQueries({
			queryKey: ['medical-record', patientId],
		});
	}, [patientId, queryClient]);

	const {
		mutateAsync: patchAssessmentAsync,
		isPending: isAssessmentPending,
	} = usePatchMedicalRecordAssessment(invalidateRecord);
	const { mutateAsync: patchPlanAsync, isPending: isPlanPending } =
		usePatchMedicalRecordPlan(invalidateRecord);

	const isPending = isAssessmentPending || isPlanPending;

	const set = <K extends keyof RecordFormState>(
		key: K,
		value: RecordFormState[K],
	) => setForm((prev) => ({ ...prev, [key]: value }));

	const hasChanges = useMemo(() => {
		const assessmentChanged =
			!!assessment &&
			getChangedKeys(ASSESSMENT_KEYS, form, baselineForm).length > 0;
		const planChanged =
			!!plan && getChangedKeys(PLAN_KEYS, form, baselineForm).length > 0;
		return assessmentChanged || planChanged;
	}, [assessment, plan, form, baselineForm]);

	const runSave = useCallback(async () => {
		if (demoMode) {
			setBaselineForm(form);
			return;
		}

		const tasks: Promise<unknown>[] = [];

		if (assessment) {
			const body = buildAssessmentBody(form, baselineForm);
			if (Object.keys(body).length > 0) {
				tasks.push(
					patchAssessmentAsync({
						recordId,
						assessmentId: assessment.id,
						body,
					}),
				);
			}
		}

		if (plan) {
			const body = buildPlanBody(form, baselineForm);
			if (Object.keys(body).length > 0) {
				tasks.push(patchPlanAsync({ recordId, planId: plan.id, body }));
			}
		}

		if (tasks.length === 0) return;

		await Promise.all(tasks);
		setBaselineForm(form);
	}, [
		assessment,
		plan,
		form,
		baselineForm,
		demoMode,
		recordId,
		patchAssessmentAsync,
		patchPlanAsync,
	]);

	const handleSave = useCallback(() => {
		runSave()
			.then(() => toast.success('Prontuário atualizado com sucesso.'))
			.catch((error) =>
				toast.error(
					getErrorMessage(
						error,
						'Erro ao salvar prontuário. Tente novamente.',
					),
				),
			);
	}, [runSave]);

	const handleSaveAsync = useCallback(() => runSave(), [runSave]);

	useEffect(() => {
		if (isLoading || isError) {
			onEditorStateChange?.(null);
			return;
		}

		onEditorStateChange?.({
			hasChanges,
			isPending,
			save: handleSave,
			saveAsync: handleSaveAsync,
		});
	}, [
		hasChanges,
		isPending,
		handleSave,
		handleSaveAsync,
		isLoading,
		isError,
		onEditorStateChange,
	]);

	const titleClass = SECTION_TITLE_CLASS[variant];

	return (
		<div
			className="min-w-0 space-y-6 pb-6"
			{...(demoMode
				? { 'data-tutorial-demo': 'review-record-fields' }
				: {})}
		>
			{/* Histórico clínico */}
			<section className="space-y-3">
				<h3 className={titleClass}>Histórico clínico</h3>
				{assessment ? (
					<div className="grid grid-cols-1 gap-3">
						<SessionFieldContainer
							label="Impressão clínica"
							value={form.clinical_impression}
							onChange={(v) => set('clinical_impression', v)}
							placeholder="Impressão clínica geral..."
							rows={4}
							variant={variant}
							animateValue={animateValues}
						/>
						<SessionFieldContainer
							label="Diagnóstico diferencial"
							value={form.differential_diagnosis}
							onChange={(v) => set('differential_diagnosis', v)}
							placeholder="Hipóteses a descartar/observar..."
							rows={3}
							variant={variant}
							animateValue={animateValues}
						/>
						<SessionFieldContainer
							label="Instrumentos utilizados"
							value={form.instruments_used}
							onChange={(v) => set('instruments_used', v)}
							placeholder="Instrumentos e métodos de avaliação..."
							rows={2}
							variant={variant}
							animateValue={animateValues}
						/>
						<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
							<SessionFieldContainer
								label="CID-10 principal"
								value={form.icd10_primary}
								onChange={(v) => set('icd10_primary', v)}
								placeholder="Ex.: F41.1"
								rows={1}
								variant={variant}
								animateValue={animateValues}
							/>
							<SessionFieldContainer
								label="CID-10 secundário"
								value={form.icd10_secondary}
								onChange={(v) => set('icd10_secondary', v)}
								placeholder="Ex.: F32.0"
								rows={1}
								variant={variant}
								animateValue={animateValues}
							/>
						</div>
						{/*
						<div className="flex flex-wrap items-start gap-3">
							<SessionFieldContainer
								label="BAI (escore)"
								value={form.bai_score}
								onChange={(v) => set('bai_score', v)}
								placeholder="0–63"
								rows={1}
								variant={variant}
								animateValue={animateValues}
							/>
							<InlineSelectField
								label="BAI (nível)"
								value={form.bai_level}
								options={SEVERITY_OPTIONS}
								onChange={(v) => set('bai_level', v)}
								variant={variant}
							/>
							<SessionFieldContainer
								label="BDI (escore)"
								value={form.bdi_score}
								onChange={(v) => set('bdi_score', v)}
								placeholder="0–63"
								rows={1}
								variant={variant}
								animateValue={animateValues}
							/>
							<InlineSelectField
								label="BDI (nível)"
								value={form.bdi_level}
								options={SEVERITY_OPTIONS}
								onChange={(v) => set('bdi_level', v)}
								variant={variant}
							/>
						</div>
						*/}
					</div>
				) : (
					<p className={EMPTY_SECTION_CLASS[variant]}>
						Nenhuma avaliação diagnóstica disponível para edição.
					</p>
				)}
			</section>

			{/* Plano terapêutico */}
			<section className="space-y-3">
				<h3 className={titleClass}>Plano terapêutico</h3>
				{plan ? (
					<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
						<div className="sm:col-span-2">
							<SessionFieldContainer
								label="Abordagem teórica"
								value={form.theoretical_approach}
								onChange={(v) => set('theoretical_approach', v)}
								placeholder="Ex.: TCC"
								rows={1}
								variant={variant}
								animateValue={animateValues}
							/>
						</div>
						<SessionFieldContainer
							label="Frequência"
							value={form.frequency}
							onChange={(v) => set('frequency', v)}
							placeholder="Ex.: Semanal"
							rows={1}
							variant={variant}
							animateValue={animateValues}
						/>
						<SessionFieldContainer
							label="Formato"
							value={form.format}
							onChange={(v) => set('format', v)}
							placeholder="Ex.: Individual online"
							rows={1}
							variant={variant}
							animateValue={animateValues}
						/>
						<SessionFieldContainer
							label="Duração da sessão (min)"
							value={form.session_duration_min}
							onChange={(v) => set('session_duration_min', v)}
							placeholder="Ex.: 50"
							rows={1}
							variant={variant}
							animateValue={animateValues}
						/>
						<SessionFieldContainer
							label="Duração estimada"
							value={form.estimated_duration}
							onChange={(v) => set('estimated_duration', v)}
							placeholder="Ex.: 6 meses"
							rows={1}
							variant={variant}
							animateValue={animateValues}
						/>
						<div className="sm:col-span-2">
							<SessionFieldContainer
								label="Objetivos terapêuticos"
								value={form.objectives}
								onChange={(v) => set('objectives', v)}
								placeholder="Objetivos do tratamento..."
								rows={4}
								variant={variant}
								animateValue={animateValues}
							/>
						</div>
						<div className="sm:col-span-2">
							<SessionFieldContainer
								label="Técnicas previstas"
								value={form.planned_techniques}
								onChange={(v) => set('planned_techniques', v)}
								placeholder="Técnicas e intervenções planejadas..."
								rows={4}
								variant={variant}
								animateValue={animateValues}
							/>
						</div>
						<div className="sm:col-span-2">
							<SessionFieldContainer
								label="Encaminhamentos"
								value={form.referrals}
								onChange={(v) => set('referrals', v)}
								placeholder="Encaminhamentos complementares..."
								rows={2}
								variant={variant}
								animateValue={animateValues}
							/>
						</div>
					</div>
				) : (
					<p className={EMPTY_SECTION_CLASS[variant]}>
						Nenhum plano terapêutico disponível para edição.
					</p>
				)}
			</section>
		</div>
	);
};

export { ProntuarioEditableMedicalRecordView };
